import { useCallback, useEffect, useState } from 'react'

import { api } from '@/lib/api'
import type { GuardianStatus } from '@/lib/types'

/**
 * Surveillance automatique du capital, et kill switch.
 *
 * Quand un seuil est franchi, le serveur ferme toutes les positions et bloque
 * les envois. Ce bandeau rend l'état visible en permanence : un blocage qu'on
 * ne voit pas se traduirait par des ordres refusés sans qu'on comprenne
 * pourquoi.
 *
 * Le réarmement est **manuel et volontaire** : c'est le seul moyen de
 * s'assurer qu'un humain a vu ce qui s'est passé avant que ça reprenne.
 */
export function GuardianBanner({
  brokerReady,
  readOnly = false,
}: {
  brokerReady: boolean
  /**
   * Masque le bouton « Tout couper ».
   *
   * Le bandeau sert à deux endroits : dans les Paramètres pour RAPPELER les
   * seuils, et dans Positions pour AGIR. Un bouton de coupure d'urgence
   * dupliqué sur deux écrans est un bouton qu'on finit par presser au mauvais
   * moment ; il ne vit que là où se trouvent les positions.
   */
  readOnly?: boolean
}) {
  const [status, setStatus] = useState<GuardianStatus | null>(null)
  const [busy, setBusy] = useState(false)
  const [confirmStop, setConfirmStop] = useState(false)

  const load = useCallback(async () => {
    try {
      setStatus(await api.guardian())
    } catch {
      setStatus(null)
    }
  }, [])

  useEffect(() => {
    if (!brokerReady) return
    void load()
    const timer = window.setInterval(load, 5000)
    return () => window.clearInterval(timer)
  }, [brokerReady, load])

  if (!brokerReady || !status) return null

  async function act(action: 'rearm' | 'trip') {
    setBusy(true)
    try {
      setStatus(action === 'rearm' ? await api.rearmGuardian() : await api.tripGuardian())
      setConfirmStop(false)
    } finally {
      setBusy(false)
    }
  }

  if (status.tripped) {
    return (
      <div className="rounded-lg border border-down bg-down/15 p-3">
        <p className="text-[13px] font-bold text-down">Positions fermées — envois bloqués</p>
        {status.detail && (
          <p className="mt-1 text-[12px] leading-relaxed text-ink">{status.detail}</p>
        )}
        <button
          type="button"
          onClick={() => act('rearm')}
          disabled={busy}
          className="mt-3 min-h-touch w-full rounded-md border border-down bg-transparent text-[13px] font-semibold text-down disabled:opacity-50"
        >
          {busy ? '…' : 'Réarmer et autoriser de nouveau les ordres'}
        </button>
      </div>
    )
  }

  return (
    <div className="flex items-center justify-between gap-2 rounded-lg border border-border bg-panel px-3 py-2">
      {/* Formulation courte : sur un téléphone, la phrase complète prenait
          deux lignes en haut de l'écran, au détriment du graphique. */}
      <p className="text-[11px] leading-tight text-ink-muted">
        Coupure auto <span className="num text-ink">±{status.swing_limit_pct} %</span> du
        capital · <span className="num text-ink">−{status.daily_loss_limit_pct} %</span> sur la
        journée
        {!status.enabled && <span className="text-warn"> (désactivée)</span>}
      </p>

      {readOnly ? null : confirmStop ? (
        <div className="flex shrink-0 gap-1">
          <button
            type="button"
            onClick={() => act('trip')}
            disabled={busy}
            className="min-h-touch rounded bg-down px-2 text-[11px] font-bold text-white disabled:opacity-50"
          >
            {busy ? '…' : 'Tout fermer'}
          </button>
          <button
            type="button"
            onClick={() => setConfirmStop(false)}
            className="min-h-touch rounded bg-elevated px-2 text-[11px] text-ink-muted"
          >
            Non
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setConfirmStop(true)}
          className="min-h-touch shrink-0 rounded border border-down/50 px-2 text-[11px] font-semibold text-down"
        >
          Tout couper
        </button>
      )}
    </div>
  )
}
